import { useState } from "react";
import { CompanyEntryDto } from "@advanced-sales-technologies/backend-client/models/CompanyEntryDto";
import { useTranslation } from "react-i18next";
import { HiOutlinePlus } from "react-icons/hi2";

import Button, { ButtonProps } from "@/components/atoms/Button";
import Modal from "@/components/molecules/Modal";
import AddToStaticListModal from "@/components/molecules/modals/AddToStaticListModal";
import CreateStaticListModal from "@/components/molecules/modals/CreateStaticListModal";

enum ModalType {
  AddToList = "AddToList",
  CreateList = "CreateList",
}

type Props = {
  selectedCompanies: (CompanyEntryDto | string)[];
  hasLists: boolean;
  resetTableSelection?: () => void;
  button?: Omit<ButtonProps, "onClick">;
};

const StaticModalHandler = ({ selectedCompanies, hasLists, resetTableSelection, button }: Props) => {
  const { t } = useTranslation();
  const [activeModal, setActiveModal] = useState<ModalType | null>(null);
  const [listName, setListName] = useState<string>("");

  const openAddToListModal = () => {
    if (hasLists) {
      setActiveModal(ModalType.AddToList);
    } else {
      setActiveModal(ModalType.CreateList);
    }
  };

  const handleOpenCreateListModal = (name: string) => {
    setListName(name);
    setActiveModal(ModalType.CreateList);
  };

  const closeModal = () => {
    if (hasLists && activeModal === ModalType.CreateList) {
      setActiveModal(ModalType.AddToList);
    } else {
      setActiveModal(null);
    }
    setListName("");
  };

  const closeAllModals = () => {
    setActiveModal(null);
    setListName("");
  };

  return (
    <>
      <Button
        onClick={() => openAddToListModal()}
        trailingIcon={<HiOutlinePlus className={"size-5"} />}
        label={t("static.add", { count: selectedCompanies.length })}
        data-testid="open-modal-button"
        {...button}
      />

      <Modal
        isOpen={activeModal === ModalType.AddToList}
        onClose={() => closeAllModals()}
        content={AddToStaticListModal}
        contentProps={{
          selectedCompanies,
          openCreateListModal: handleOpenCreateListModal,
          onSuccess: () => resetTableSelection?.(),
        }}
      />

      <Modal
        isOpen={activeModal === ModalType.CreateList}
        onClose={() => closeModal()}
        content={CreateStaticListModal}
        contentProps={{
          listName,
          selectedCompanies,
          onSuccess: () => {
            resetTableSelection?.();
            closeAllModals();
          },
        }}
      />
    </>
  );
};

export default StaticModalHandler;
